/**
 * Draining the outbox (TICKET FE-016).
 *
 * Strictly one at a time, strictly in order, stopping at the first network
 * failure. Sending 10 units out before the receive that put them there fails
 * with "stock insuffisant"; the other way round it works. Draining in parallel,
 * or skipping the item that blocks, turns a reconnection into corrupted stock.
 *
 * See docs/adr/0009-offline-outbox-and-service-worker.md.
 */

import { ApiError, apiFetch } from "@/api/client";
import * as outbox from "./outbox";
import type { OutboxEntry } from "./outbox";

export type SyncState = "IDLE" | "OFFLINE" | "SYNCING" | "BLOCKED";

export interface SyncSnapshot {
  state: SyncState;
  online: boolean;
  entries: OutboxEntry[];
  lastSyncedAt: number | null;
}

type Listener = (snapshot: SyncSnapshot) => void;

const listeners = new Set<Listener>();
let entries: OutboxEntry[] = [];
let syncing = false;
let lastSyncedAt: number | null = null;
let onDrained: (() => void) | null = null;

/** Told what to refresh once something lands. Set once, by the app. */
export function setInvalidator(handler: (() => void) | null): void {
  onDrained = handler;
}

function online(): boolean {
  return typeof navigator === "undefined" ? true : navigator.onLine;
}

function snapshot(): SyncSnapshot {
  const blocked = outbox.isBlocked(entries);
  const pending = entries.some((entry) => entry.status === "PENDING");
  let state: SyncState = "IDLE";
  if (syncing) state = "SYNCING";
  else if (blocked) state = "BLOCKED";
  else if (!online() && pending) state = "OFFLINE";
  else if (!online()) state = "OFFLINE";
  return { state, online: online(), entries, lastSyncedAt };
}

function emit(): void {
  const current = snapshot();
  for (const listener of listeners) listener(current);
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  listener(snapshot());
  return () => listeners.delete(listener);
}

export async function refresh(): Promise<void> {
  entries = await outbox.list();
  emit();
}

/**
 * Queue one operation.
 *
 * Only ever called for a request that carries an idempotency key: that is what
 * makes replaying it safe, and the ADR explains why nothing else is queued.
 */
export async function enqueue(entry: outbox.NewEntry): Promise<OutboxEntry> {
  const stored = await outbox.enqueue(entry);
  await refresh();
  return stored;
}

async function send(entry: OutboxEntry): Promise<void> {
  if (entry.file) {
    const form = new FormData();
    form.append(entry.file.field, entry.file.blob, entry.file.name);
    for (const [key, value] of Object.entries(
      (entry.body as Record<string, unknown> | undefined) ?? {},
    )) {
      if (value !== undefined && value !== null) form.append(key, String(value));
    }
    await apiFetch(entry.path, { method: entry.method, formData: form });
    return;
  }
  await apiFetch(entry.path, { method: entry.method, body: entry.body });
}

/**
 * Send what can be sent.
 *
 * Returns when the queue is empty, or when something stops it: a network
 * failure (retry later, keep the order) or a refusal (keep it, show it).
 */
export async function drain(): Promise<void> {
  if (syncing || !online()) {
    emit();
    return;
  }
  syncing = true;
  emit();

  try {
    for (;;) {
      const next = await outbox.head();
      if (!next) break;

      await outbox.update({ ...next, attempts: next.attempts + 1 });
      try {
        await send(next);
        await outbox.remove(next.id);
        lastSyncedAt = Date.now();
        await refresh();
        onDrained?.();
      } catch (caught) {
        const error = caught as ApiError;
        // A network failure is not a refusal: the operation may never have been
        // seen. It stays PENDING, at the front, and the queue stops so nothing
        // overtakes it.
        if (error?.status === 0 || !online()) {
          await outbox.update({ ...next, attempts: next.attempts + 1 });
          break;
        }
        // A 5xx is the server having a bad moment, not the operation being
        // wrong. Same treatment: keep it, stop, try again later.
        if (error?.status >= 500) break;

        await outbox.update({
          ...next,
          status: error?.status === 409 ? "CONFLICT" : "FAILED",
          error: {
            code: error?.code ?? "error",
            message: error?.message ?? "Refusé par le serveur.",
            status: error?.status ?? 0,
          },
        });
        await refresh();
        // Stop at the first refusal too: the operations behind it were queued
        // assuming this one had happened.
        break;
      }
    }
  } finally {
    syncing = false;
    await refresh();
  }
}

let started = false;

/** Wire the queue to the browser's idea of connectivity. Idempotent. */
export function start(): void {
  if (started || typeof window === "undefined") return;
  started = true;

  window.addEventListener("online", () => {
    void drain();
  });
  window.addEventListener("offline", () => emit());
  // A tab that comes back to the foreground is the most common moment for a
  // phone to have regained signal without firing `online`.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void drain();
  });

  void refresh().then(() => drain());
}

export async function retry(id: number): Promise<void> {
  await outbox.retry(id);
  await refresh();
  await drain();
}

export async function discard(id: number): Promise<void> {
  await outbox.remove(id);
  await refresh();
}
